import { SelectChangeEvent } from "@mui/material";
import { isEmpty } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
  STATUS_BADGE_TYPE,
  StatusBadgeType,
} from "../../../../../../../../components";
import {
  BILLING_DETAILS_SITE_CORE_VALUE,
  BILLING_TYPE,
} from "../../../../../../../../helpers/constants";
import {
  getBillingScheduleExecutionStatus,
  isActiveStatusAndImmediateBillingType,
  isBilledAndActivityBasedBillingType,
  isImmediateBilling,
  isNotEmpty,
  isOpenStatusAndImmediateBillingType,
  isStatusActive,
  isStatusClosed,
  isStatusConcon,
  isStatusInActive,
} from "../../../../../../../../helpers/utils";
import { useGridContainer } from "../../../../../../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import { BILLING_TYPE_CLEAR_ACTIONS } from "../../../../../../../createProgram/helper";
import { ProjectFeeTabFormKey } from "../../../../../../modelsAndSchemas/Models";
import { projectFeeDetailsCoreKeys } from "../../../../../../siteCore/SiteCoreKeys";
import { BillingTypeFieldInvoiceProps } from "./BillingTypeFieldInvoice";
import { checkAllowBillingToChangeInvoice } from "./helper";

const feeFormKey = `${ProjectFeeTabFormKey.fee}`;
const feeWithFormatFormKey = `${ProjectFeeTabFormKey.feeWithFormat}`;
const billingTriggerIdFormKey = `${ProjectFeeTabFormKey.billingTriggerId}`;
const billingSchedulesFormKey = `${ProjectFeeTabFormKey.billingSchedules}`;
const schedulePeriodFormKey = `${ProjectFeeTabFormKey.schedulePeriod}`;
const schedulesFormKey = `${ProjectFeeTabFormKey.schedules}`;
const feePercentageKeyInvoice = `${ProjectFeeTabFormKey.feePercentage}`;
const amountKeyInvoice = `${ProjectFeeTabFormKey.amount}`;
const amountWithFormatKeyInvoice = `${ProjectFeeTabFormKey.amountWithFormat}`;
const scheduleFrequencyFormKey = `${ProjectFeeTabFormKey.scheduleFrequency}`;
const feePaymentTypeFormKey = `${ProjectFeeTabFormKey.feePaymentType}`;
const milestonesFormKey = `${ProjectFeeTabFormKey.milestones}`;
const activitiesFormKey = `${ProjectFeeTabFormKey.activities}`;
const minimumTransferFeeFormKey = `${ProjectFeeTabFormKey.minimumTransferFee}`;
const minimumTransferFeeWithFormatFormKey = `${ProjectFeeTabFormKey.minimumTransferFeeWithFormat}`;
const earlyPayDaysFormKey = `${ProjectFeeTabFormKey.earlyPayDays}`;
const earlyPayPercentageFormKey = `${ProjectFeeTabFormKey.earlyPayPercentage}`;
const proBonoCategoryFormKey = `${ProjectFeeTabFormKey.proBonoCategoryId}`;
const billingTypeFormKey = `${ProjectFeeTabFormKey.billingTypeId}`;

export const useBillingTypeFieldInvoice = (
  props: BillingTypeFieldInvoiceProps,
) => {
  const {
    currencyId,
    defaultUserCurrency,
    currenciesList,
    forceUpdate,
    pageId,
    setShowWarningOnBillingChange,
    projectStatus,
    projectFeeDetailsData,
    currencyFormat,
  } = props;
  const { gridSizeMedium: gridSize } = useGridContainer();

  const {
    control,
    setValue,
    getValues,
    clearErrors,
    formState: { errors },
  } = useFormContext();

  const isProBonoSelectedFirstRef = useRef(false);
  const [isProgramFeeDisabled, setIsProgramFeeDisabled] = useState(false);
  const [isBillingTypeDisabled, setIsBillingTypeDisabled] = useState(false);
  const [lastBillingType, setLastBillingType] = useState("");
  const [isChangeFirstTimeBillingType, setIsChangeFirstTimeBillingType] =
    useState(true);

  const {
    commonLabels,
    fetchDropdownValues,
    fetchLabel,
    fetchValidationMessage,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const {
    projectFeeDetails,
    earlyPayDays,
    earlyPayPercentage,
    minimumTransferFee,
    billingTriggerDropDown,
    currencyDropDown,
    projectFee,
    billingTriggerList,
    billingTypeList,
    billingTypeDropDown,
    proBonoCategoryDropDown,
    proBonoCategoryList,
    earlyPayPercentageToolTipMessage,
  } = projectFeeDetailsCoreKeys;

  const handleConfirmationStatusTrue = () => {
    setIsChangeFirstTimeBillingType(false);
    clearOnBillingTypeChangeInvoice();
  };

  const handleConfirmationStatusFalse = () => {
    setValue(billingTypeFormKey, lastBillingType);
  };

  const getLabel = (key: string) => {
    return fetchLabel(projectFeeDetails, key);
  };

  const billingTypeWatch = useWatch({
    control,
    name: billingTypeFormKey,
  });

  const projectStatusValue = (projectStatus ??
    STATUS_BADGE_TYPE.open) as StatusBadgeType;
  const billingSchedules = getValues(billingSchedulesFormKey);

  const proBonoCategoryWatch = useWatch({
    control,
    name: proBonoCategoryFormKey,
  });

  const shouldIgnoreZeroValidationForFee = useMemo(
    () =>
      isNotEmpty(proBonoCategoryWatch) && isImmediateBilling(billingTypeWatch),
    [proBonoCategoryWatch, billingTypeWatch],
  );

  useEffect(() => {
    handleProBonoFieldChange(proBonoCategoryWatch);
  }, [proBonoCategoryWatch]);

  const isAllSchedulesExecuted = useMemo(
    () => getBillingScheduleExecutionStatus(billingSchedules).allExecuted,
    [billingSchedules],
  );

  const isDisabledForActiveStatusAndImmediateBillingType = useMemo(() => {
    return (
      projectFeeDetailsData?.isInvoiced &&
      (isActiveStatusAndImmediateBillingType(
        projectStatusValue,
        billingTypeWatch,
      ) ||
        isOpenStatusAndImmediateBillingType(
          projectStatusValue,
          billingTypeWatch,
        ))
    );
  }, [projectStatusValue, projectFeeDetailsData?.isInvoiced, billingTypeWatch]);

  const isProjectStatusActiveAlready = useMemo(
    () =>
      isStatusClosed(projectStatusValue) ||
      isStatusActive(projectStatusValue) ||
      isStatusInActive(projectStatusValue) ||
      isStatusConcon(projectStatusValue),
    [projectStatusValue],
  );

  const isActivityBasedBilled = useMemo(() => {
    return isBilledAndActivityBasedBillingType(
      billingTypeWatch,
      projectFeeDetailsData?.isBilled,
    );
  }, [projectFeeDetailsData?.isBilled, billingTypeWatch]);

  const clearMilestoneBasedValueInvoice = () => {
    setValue(milestonesFormKey, "");
    setValue(activitiesFormKey, "");
    setValue(feePaymentTypeFormKey, "");
    setValue(feePercentageKeyInvoice, "");
    setValue(amountKeyInvoice, "");
    setValue(amountWithFormatKeyInvoice, "");
    setValue(billingSchedulesFormKey, []);
  };

  const clearScheduleBasedValueInvoice = () => {
    setValue(schedulePeriodFormKey, "");
    setValue(schedulesFormKey, "");
    setValue(feePaymentTypeFormKey, "");
    setValue(feePercentageKeyInvoice, "");
    setValue(amountKeyInvoice, "");
    setValue(amountWithFormatKeyInvoice, "");
    setValue(scheduleFrequencyFormKey, "");
    setValue(billingSchedulesFormKey, []);
  };

  const clearActivityBasedValueInvoice = () => {
    setValue(billingTriggerIdFormKey, "");
  };

  const shouldDisableProBonoField = useMemo(() => {
    return (
      isDisabledForActiveStatusAndImmediateBillingType ||
      (!isImmediateBilling(billingTypeWatch) && isNotEmpty(billingTypeWatch))
    );
  }, [isDisabledForActiveStatusAndImmediateBillingType, billingTypeWatch]);

  const shouldDisableProgramFee = useMemo(() => {
    return (
      isDisabledForActiveStatusAndImmediateBillingType ||
      isAllSchedulesExecuted ||
      isActivityBasedBilled ||
      isProgramFeeDisabled
    );
  }, [
    isDisabledForActiveStatusAndImmediateBillingType,
    isAllSchedulesExecuted,
    isActivityBasedBilled,
    isProgramFeeDisabled,
  ]);

  const shouldDisableBillingType = useMemo(() => {
    return (
      isDisabledForActiveStatusAndImmediateBillingType ||
      isProjectStatusActiveAlready ||
      isBillingTypeDisabled
    );
  }, [
    isDisabledForActiveStatusAndImmediateBillingType,
    isProjectStatusActiveAlready,
    isBillingTypeDisabled,
  ]);

  const shouldDisableOtherFields = useMemo(() => {
    return (
      isDisabledForActiveStatusAndImmediateBillingType ||
      isAllSchedulesExecuted ||
      isActivityBasedBilled
    );
  }, [
    isDisabledForActiveStatusAndImmediateBillingType,
    isAllSchedulesExecuted,
    isActivityBasedBilled,
  ]);

  const handleProBonoFieldChange = (value: string) => {
    const { billingTypeId } = getValues();

    if (isNotEmpty(value)) {
      // ProBono category selected - set fee to 0 and disable program fee
      setValue(feeWithFormatFormKey, 0);
      setValue(feeFormKey, 0);
      setIsProgramFeeDisabled(true);

      if (isEmpty(billingTypeId)) {
        isProBonoSelectedFirstRef.current = true;
        // Case 2: ProBono selected first - set billing type to IMMEDIATE and disable billing type field
        setValue(
          billingTypeFormKey,
          BILLING_DETAILS_SITE_CORE_VALUE.billingTypeImmediateBilling,
        );
        setIsBillingTypeDisabled(true);
        if (errors[billingTypeFormKey]) {
          clearErrors(billingTypeFormKey);
        }

        if (errors[feeWithFormatFormKey]) {
          clearErrors(feeWithFormatFormKey);
        }
      }
    } else {
      // ProBono category cleared - reset fields and enable them
      if (isProBonoSelectedFirstRef.current) {
        setValue(billingTypeFormKey, "");
        setIsBillingTypeDisabled(false);
      }
      setValue(feeWithFormatFormKey, "");
      setIsProgramFeeDisabled(false);
    }

    forceUpdate();
  };

  const clearOnBillingTypeChangeInvoice = () => {
    const actions = BILLING_TYPE_CLEAR_ACTIONS[billingTypeWatch] ?? [];
    if (actions.includes(BILLING_TYPE.MILESTONE))
      clearMilestoneBasedValueInvoice();
    if (actions.includes(BILLING_TYPE.SCHEDULE))
      clearScheduleBasedValueInvoice();
    if (actions.includes(BILLING_TYPE.ACTIVITY))
      clearActivityBasedValueInvoice();
    forceUpdate();
  };

  useEffect(() => {
    if (!isImmediateBilling(billingTypeWatch)) {
      setValue(proBonoCategoryFormKey, "");
    }
    if (isEmpty(lastBillingType)) setLastBillingType(billingTypeWatch);
  }, [billingTypeWatch]);

  const onBillingTypeChange = (e: SelectChangeEvent<unknown>) => {
    let isPauseLastSelection = false;
    const [billingSchedules, billingType] =
      getValues([billingSchedulesFormKey, billingTypeFormKey]) ?? [];
    if (
      !checkAllowBillingToChangeInvoice(
        billingSchedules,
        billingType,
        isChangeFirstTimeBillingType,
      ) &&
      isNotEmpty(billingType)
    ) {
      setShowWarningOnBillingChange(true);
      isPauseLastSelection = true;
    } else {
      clearOnBillingTypeChangeInvoice();
      setIsChangeFirstTimeBillingType(false);
    }
    if (!isPauseLastSelection) {
      const billingType = e.target.value as string;
      setLastBillingType(billingType);
    }

    forceUpdate();
  };

  return {
    control,
    gridSize,
    pageId,
    getLabel,
    fetchDropdownValues,
    fetchValidationMessage,
    commonLabels,
    commonARIALabel,
    billingTypeFormKey,
    billingTypeWatch,
    isProjectStatusActiveAlready,
    onBillingTypeChange,
    billingTriggerIdFormKey,
    feeWithFormatFormKey,
    feeFormKey,
    currenciesList,
    defaultUserCurrency,
    currencyId,
    minimumTransferFeeWithFormatFormKey,
    minimumTransferFeeFormKey,
    earlyPayDaysFormKey,
    earlyPayPercentageFormKey,
    proBonoCategoryFormKey,
    setValue,
    forceUpdate,
    projectFeeDetails,
    earlyPayDays,
    earlyPayPercentage,
    minimumTransferFee,
    billingTriggerDropDown,
    currencyDropDown,
    projectFee,
    billingTriggerList,
    billingTypeList,
    billingTypeDropDown,
    proBonoCategoryDropDown,
    proBonoCategoryList,
    earlyPayPercentageToolTipMessage,
    currencyFormat,
    shouldIgnoreZeroValidationForFee,
    shouldDisableProBonoField,
    shouldDisableProgramFee,
    shouldDisableBillingType,
    shouldDisableOtherFields,
    handleConfirmationStatusTrue,
    handleConfirmationStatusFalse,
  };
};
